import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import styled from "styled-components";
import ScrollableOverflow from "./ScrollableOverflow";
import { useEffect, useState } from "react";
import { Link } from "gatsby";

const CategoryCarouselWrapper = styled.div`
  position: relative;
`;

const CategoryCarouselHeadline = styled.h2`
  margin-bottom: var(--spacing-1_5);

  @media (min-width: 933px) {
    margin-bottom: var(--spacing-3);
  }
`;

const CategoryCard = styled(Link)`
  display: flex;
  flex-direction: column;
  width: 155px;
  margin-right: var(--spacing-1);

  &:last-child {
    margin-right: 0;
  }

  p {
    margin-bottom: 0;
  }

  @media (min-width: 933px) {
    width: 300px;
    margin-right: var(--spacing-1_5);
  }
`;

const CategoryImage = styled.div`
  aspect-ratio: 15/13;
  max-height: 260px;
  width: 100%;
  margin-bottom: var(--spacing-0_75);

  @media (min-width: 933px) {
    margin-bottom: var(--spacing-1);
  }

  img {
    -webkit-user-drag: none;
    -khtml-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
    user-drag: none;
    transition: all 0.3s ease-in-out !important;
  }

  &:hover {
    img {
      transform: scale(1.05);
    }
  }
`;

type PropTypes = {
  headline?: {
    childMarkdownRemark: {
      html: string;
    };
  };
  productCategories?: {
    shortName: string;
    contentful_id: string;
    cardImage: { title: string; gatsbyImageData: IGatsbyImageData };
    shopLandingReference: {
      slug: string;
    };
  }[];
};

const CategoryCarousel = ({ headline, productCategories }: PropTypes) => {
  const [width, setWidth] = useState(155);

  const calculateWidth = () => {
    if (window.innerWidth >= 933) {
      const wrapperWidth =
        document.querySelector(".CategoryCarouselWrapper")?.clientWidth || 300;
      const gutter = 16 * 1.5 * 3;
      setWidth((wrapperWidth - gutter) * 0.25);
    } else {
      const wrapperWidth =
        document.querySelector(".CategoryCarouselWrapper")?.clientWidth || 300;
      const gutter = 16 * 1.5;
      setWidth((wrapperWidth - gutter) * 0.5);
    }
  };

  useEffect(() => {
    calculateWidth();

    const handleResize = () => {
      calculateWidth();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <CategoryCarouselWrapper className="CategoryCarouselWrapper">
      <CategoryCarouselHeadline
        className="typography-headline2"
        dangerouslySetInnerHTML={{
          __html: headline?.childMarkdownRemark?.html || "",
        }}
      />
      <ScrollableOverflow bleedRight={true} bleedLeft={true}>
        {productCategories?.map((category) => {
          return (
            <CategoryCard
              to={category?.shopLandingReference?.slug}
              title={`Navigate to ${category.shortName} category`}
              style={{
                width,
              }}
              key={category.contentful_id}
            >
              <CategoryImage>
                <GatsbyImage
                  image={category.cardImage?.gatsbyImageData}
                  alt={category.cardImage.title}
                  draggable="false"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </CategoryImage>
              <p className="typography-label2">{category.shortName}</p>
            </CategoryCard>
          );
        })}
      </ScrollableOverflow>
    </CategoryCarouselWrapper>
  );
};

export default CategoryCarousel;
